import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LicenseType, OrganizationSignup as IOrganizationSignup } from '../../types/license.types';
import { AuthService } from '../../services/auth.service';
import { LicenseService } from '../../services/license.service';
import { toast } from 'react-hot-toast';
import { BuildingOfficeIcon, CreditCardIcon, InformationCircleIcon, ArrowRightIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { deriveKeys, encryptVaultKey, encryptKeyData } from '../../utils/encryption';
import Navbar from '../common/Navbar';
import PaymentForm from '../payment/PaymentForm';
import Input from '../common/Input';
import PasswordStrengthMeter from '../common/PasswordStrengthMeter';
import { generateStrongPassword } from '../../utils/passwordGenerator';

interface SignupFormData {
    email: string;
    password: string;
    confirmPassword: string;
    masterPasswordHint?: string;
    organizationName: string;
    licenseCount: number;
    memberLimit: number;
}

const LICENSE_PRICE_PER_USER = Number(process.env.REACT_APP_LICENSE_PRICE_PER_USER) || 6;
const FREE_LICENSE_COUNT = Number(process.env.REACT_APP_FREE_LICENSE_COUNT) || 3;

const OrganizationSignup: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState<SignupFormData>({
        email: '',
        password: '',
        confirmPassword: '',
        masterPasswordHint: '',
        organizationName: '',
        licenseCount: FREE_LICENSE_COUNT,
        memberLimit: FREE_LICENSE_COUNT
    });

    const calculatePrice = (licenses: number): number => {
        if (licenses <= FREE_LICENSE_COUNT) return 0;
        return licenses * LICENSE_PRICE_PER_USER;
    };

    // Add state for payment intent ID
    const [paymentIntentId, setPaymentIntentId] = useState<string>('');

    // Get license type from URL parameters
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const type = params.get('type') as LicenseType;
        if (type && (type === 'trial' || type === 'paid')) {
            setFormData(prev => ({
                ...prev,
                licenseType: type
            }));
        }
    }, [location]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        
        if (name === 'licenseCount') {
            const numValue = Math.max(FREE_LICENSE_COUNT, parseInt(value) || FREE_LICENSE_COUNT);
            setFormData(prev => ({
                ...prev,
                [name]: numValue
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleLicenseIncrement = () => {
        setFormData(prev => ({
            ...prev,
            licenseCount: prev.licenseCount + 1
        }));
    };

    const handleLicenseDecrement = () => {
        setFormData(prev => ({
            ...prev,
            licenseCount: Math.max(FREE_LICENSE_COUNT, prev.licenseCount - 1)
        }));
    };

    const validateStep1 = async () => {
        if (!formData.email || !formData.password || !formData.confirmPassword) {
            setError('Please fill in all required fields');
            return false;
            }

            if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return false;
        }

        if (formData.password === formData.email) {
            setError('Your password cannot be the same as your email address');
            return false;
        }

        try {
            const checkUserResponse = await AuthService.checkUserExists(formData.email);
            if (checkUserResponse.exists) {
                setError('User already exists');
                return false;
            }
        } catch (error) {
            console.error('Error checking user:', error);
            setError('Something went wrong');
            return false;
        }

        return true;
    };

    const handleNextStep = async () => {
        if (currentStep === 1) {
            const isValid = await validateStep1();
            if (isValid) {
                setError(null);
                setCurrentStep(2);
            }
        }
    };

    const handlePrevStep = () => {
        setCurrentStep(1);
    };

    const handlePaymentSuccess = async (intentId: string) => {
        try {
            setIsLoading(true);

            // Generate encryption keys
            const { authKey, encryptionKey, symmetricKey } = deriveKeys(
                formData.password,
                formData.email
            );

            // Encrypt keys
            const encryptedVaultKey = encryptVaultKey(symmetricKey, encryptionKey);
            const encryptedKey = encryptKeyData(encryptionKey);

            // Register user with payment ID
            const response = await AuthService.registerOrganization({
                email: formData.email,
                authKey,
                masterPasswordHint: formData.masterPasswordHint,
                encryptedVaultKey,
                encryptedKey,
                organizationName: formData.organizationName,
                licenseType: formData.licenseCount > FREE_LICENSE_COUNT ? 'paid' : 'trial',
                memberLimit: formData.licenseCount,
                paymentIntentId: intentId
            });

            if (response.success) {
                toast.success('Account created successfully!');
                navigate('/login');
            } else {
                throw new Error(response.message || 'Registration failed');
            }
        } catch (error) {
            console.error('Registration error:', error);
            toast.error('Failed to create organization');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePaymentError = (error: string) => {
        toast.error(error);
        setShowPaymentForm(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.licenseCount > FREE_LICENSE_COUNT) {
            setShowPaymentForm(true);
            return;
        }

            setIsLoading(true);
            try {
                // Generate encryption keys
                const { authKey, encryptionKey, symmetricKey } = deriveKeys(
                    formData.password,
                    formData.email
                );

                // Encrypt keys
                const encryptedVaultKey = encryptVaultKey(symmetricKey, encryptionKey);
                const encryptedKey = encryptKeyData(encryptionKey);

                const response = await AuthService.registerOrganization({
                    email: formData.email,
                    authKey,
                    masterPasswordHint: formData.masterPasswordHint,
                    encryptedVaultKey,
                    encryptedKey,
                    organizationName: formData.organizationName,
                licenseType: formData.licenseCount > FREE_LICENSE_COUNT ? 'paid' : 'trial',
                memberLimit: formData.licenseCount
                });

                if (response.success) {
                    toast.success('Account created successfully!');
                    navigate('/login');
                }
                else {
                    setError(response.message || 'Registration failed');
                }
            } catch (error) {
                console.error('Registration error:', error);
                toast.error('Failed to create organization');
            } finally {
                setIsLoading(false);
        }
    };

    const handleGeneratePassword = () => {
        const newPassword = generateStrongPassword();
        setFormData(prev => ({
            ...prev,
            password: newPassword,
            confirmPassword: newPassword
        }));
    };

    const renderStep1 = () => (
        <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Email address
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    />
                                </div>

                                <div>
                                    <div className="flex justify-between items-center">
                                        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                            Master Password
                                        </label>
                                        <button
                                            type="button"
                                            onClick={handleGeneratePassword}
                                            className="text-sm text-blue-600 hover:text-blue-500"
                                        >
                                            Generate a Strong Password
                                        </button>
                                    </div>
                                    <Input
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        required
                                        showPasswordToggle
                                    />
                                    {formData.password && (
                                        <PasswordStrengthMeter
                                            password={formData.password}
                                            email={formData.email}
                                        />
                                    )}
                                </div>

                                <div>
                                    <Input
                                        label="Confirm Master Password"
                                        type="password"
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange}
                                        required
                                        showPasswordToggle
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Master Password Hint (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        name="masterPasswordHint"
                                        value={formData.masterPasswordHint}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    />
                                </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}

            <button
                type="button"
                onClick={handleNextStep}
                disabled={isLoading}
                className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 space-x-2"
            >
                <span>Continue</span>
                <ArrowRightIcon className="h-4 w-4" />
            </button>
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-6">
            <div>
                <label className="block text-sm font-medium text-gray-700">
                    Organization Name
                </label>
                <input
                    type="text"
                    name="organizationName"
                    required
                    value={formData.organizationName}
                    onChange={handleInputChange}
                    maxLength={100}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
            </div>

            <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                    Team Size
                </label>
                
                <div className="relative bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center space-x-4">
                        <button
                            type="button"
                            onClick={handleLicenseDecrement}
                            className="p-2 rounded-md bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            -
                        </button>
                        
                        <input
                            type="number"
                            name="licenseCount"
                            min={FREE_LICENSE_COUNT}
                            value={formData.licenseCount}
                            onChange={handleInputChange}
                            className="block w-20 text-center border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                        
                        <button
                            type="button"
                            onClick={handleLicenseIncrement}
                            className="p-2 rounded-md bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            +
                        </button>

                        <div className="flex-1 text-right">
                            <span className="text-2xl font-bold text-gray-900">
                                ${calculatePrice(formData.licenseCount)}
                            </span>
                        </div>
                    </div>

                    <div className="mt-3 flex items-start space-x-2 text-sm text-gray-500">
                        <InformationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
                        <p>
                            First {FREE_LICENSE_COUNT} users are free. When exceeding {FREE_LICENSE_COUNT} users, 
                            pricing is ${LICENSE_PRICE_PER_USER}/user for all users (including the first {FREE_LICENSE_COUNT}).
                        </p>
                    </div>

                    {formData.licenseCount > FREE_LICENSE_COUNT && (
                        <div className="mt-3 bg-blue-50 p-3 rounded-md">
                            <p className="text-sm text-blue-700">
                                Total: {formData.licenseCount} users × ${LICENSE_PRICE_PER_USER} = ${calculatePrice(formData.licenseCount)}
                            </p>
                                    </div>
                                )}
                </div>
            </div>

            <div className="flex space-x-4">
                <button
                    type="button"
                    onClick={handlePrevStep}
                    className="flex-1 flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 space-x-2"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    <span>Back</span>
                </button>

                                <button
                                    type="submit"
                                    disabled={isLoading}
                    className="flex-1 flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    {isLoading
                                        ? 'Processing...'
                        : formData.licenseCount > FREE_LICENSE_COUNT
                            ? `Continue to Payment ($${calculatePrice(formData.licenseCount)})`
                            : 'Create Free Account'}
                                </button>
            </div>
        </div>
    );

    return (
        <div>
            <Navbar />
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Create Your Team Account
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        Get started with secure password management
                    </p>

                    {/* Step indicator */}
                    <div className="mt-8 flex justify-center items-center space-x-4">
                        <div className={`flex items-center ${currentStep === 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                            <span className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${currentStep === 1 ? 'border-blue-600 bg-blue-50' : 'border-gray-300'}`}>
                                1
                            </span>
                            <span className="ml-2 text-sm font-medium">Account Setup</span>
                        </div>
                        <div className="w-8 h-px bg-gray-300"></div>
                        <div className={`flex items-center ${currentStep === 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                            <span className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${currentStep === 2 ? 'border-blue-600 bg-blue-50' : 'border-gray-300'}`}>
                                2
                            </span>
                            <span className="ml-2 text-sm font-medium">Team Setup</span>
                        </div>
                    </div>
                </div>

                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                        {showPaymentForm ? (
                            <PaymentForm
                                teamId=""
                                price={calculatePrice(formData.licenseCount)}
                                memberLimit={formData.licenseCount}
                                licenseType="standard"
                                onSuccess={handlePaymentSuccess}
                                onError={handlePaymentError}
                            />
                        ) : (
                            <form onSubmit={handleSubmit}>
                                {currentStep === 1 ? renderStep1() : renderStep2()}
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrganizationSignup; 